import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, AlertTriangle, Shield, Users } from 'lucide-react';

const TermsConditions: React.FC = () => {
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <Link
            to="/"
            className="inline-flex items-center text-primary hover:text-primary-dark transition-colors duration-200 mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-textBase">Terms & Conditions</h1>
              <p className="text-grey-600">Last updated: {currentDate}</p>
            </div>
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm p-8 space-y-8"
        >
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-semibold text-textBase mb-4">Agreement to Terms</h2>
            <p className="text-grey-600 leading-relaxed">
              These Terms and Conditions ("Terms") govern your use of the Financial Goal Tracker mobile application 
              ("App") operated by FinInteract ("Company," "we," "our," or "us"). By downloading, installing, or using 
              the App, you agree to be bound by these Terms. If you disagree with any part of these Terms, you may not 
              access or use the App.
            </p>
          </section>

          {/* App Description */}
          <section>
            <h2 className="text-2xl font-semibold text-textBase mb-4">App Description</h2>
            <p className="text-grey-600 leading-relaxed">
              Financial Goal Tracker is a mobile application designed to help users set, track, and achieve their 
              financial goals. The App allows users to input financial goals, track transactions, monitor progress, 
              and receive insights about their financial habits.
            </p>
          </section>

          {/* User Accounts */}
          <section>
            <h2 className="text-2xl font-semibold text-textBase mb-4 flex items-center">
              <Users className="w-6 h-6 mr-2 text-primary" />
              User Accounts
            </h2>
            <div className="space-y-4">
              <p className="text-grey-600 leading-relaxed">
                To use certain features of the App, you may be required to create an account. You are responsible for:
              </p>
              <ul className="list-disc list-inside text-grey-600 space-y-2 ml-4">
                <li>Providing accurate and complete information when creating your account</li>
                <li>Maintaining the security of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized use of your account</li>
                <li>Ensuring you are at least 13 years old to use the App</li>
              </ul>
            </div>
          </section>

          {/* Acceptable Use */}
          <section>
            <h2 className="text-2xl font-semibold text-textBase mb-4">Acceptable Use</h2>
            <p className="text-grey-600 leading-relaxed mb-4">
              You agree to use the App only for lawful purposes and in accordance with these Terms. You agree not to:
            </p>
            <ul className="list-disc list-inside text-grey-600 space-y-2 ml-4">
              <li>Use the App for any illegal or unauthorized purpose</li>
              <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
              <li>Interfere with or disrupt the App's functionality</li>
              <li>Upload or transmit viruses, malware, or other harmful code</li>
              <li>Use the App to store or transmit sensitive financial information of others</li>
              <li>Reverse engineer, decompile, or disassemble the App</li>
              <li>Use automated systems to access the App without our permission</li>
            </ul>
          </section>

          {/* Financial Data and Privacy */}
          <section>
            <h2 className="text-2xl font-semibold text-textBase mb-4">Financial Data and Privacy</h2>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <p className="text-grey-600 leading-relaxed mb-4">
                <strong>Important:</strong> The App is designed to help you track your financial goals and transactions. 
                You acknowledge and agree that:
              </p>
              <ul className="list-disc list-inside text-grey-600 space-y-2 ml-4">
                <li>You are responsible for the accuracy of all financial data you input</li>
                <li>The App is not a substitute for professional financial advice</li>
                <li>We do not guarantee the accuracy of financial calculations or projections</li>
                <li>Your use of the App does not create a fiduciary relationship</li>
                <li>You should consult with qualified financial professionals for investment advice</li>
              </ul>
            </div>
          </section>

          {/* Data Security and Backup */}
          <section>
            <h2 className="text-2xl font-semibold text-textBase mb-4 flex items-center">
              <Shield className="w-6 h-6 mr-2 text-primary" />
              Data Security and Backup
            </h2>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <p className="text-yellow-800 leading-relaxed mb-4">
                <strong>Data Loss Disclaimer:</strong> While we implement comprehensive security measures including 
                end-to-end encryption and regular backups, you acknowledge that:
              </p>
              <ul className="list-disc list-inside text-yellow-800 space-y-2 ml-4">
                <li>No electronic storage system is 100% secure</li>
                <li>We are not liable for any data loss, corruption, or unauthorized access</li>
                <li>You should maintain your own backups of important financial information</li>
                <li>Technical issues, system failures, or cyber attacks may result in data loss</li>
                <li>We recommend keeping physical or separate digital records of critical financial data</li>
              </ul>
            </div>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-semibold text-textBase mb-4">Intellectual Property</h2>
            <p className="text-grey-600 leading-relaxed mb-4">
              The App and its original content, features, and functionality are owned by FinInteract and are protected 
              by international copyright, trademark, patent, trade secret, and other intellectual property laws.
            </p>
            <p className="text-grey-600 leading-relaxed">
              You retain ownership of the financial data you input into the App. By using the App, you grant us a 
              limited license to use, store, and process your data solely for the purpose of providing the App's services.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-semibold text-textBase mb-4 flex items-center">
              <AlertTriangle className="w-6 h-6 mr-2 text-primary" />
              Limitation of Liability
            </h2>
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <p className="text-red-800 leading-relaxed mb-4">
                <strong>Important Limitation:</strong> To the maximum extent permitted by law, FinInteract shall not be 
                liable for any indirect, incidental, special, consequential, or punitive damages, including but not 
                limited to:
              </p>
              <ul className="list-disc list-inside text-red-800 space-y-2 ml-4">
                <li>Loss of profits, data, or business opportunities</li>
                <li>Financial losses resulting from use of the App</li>
                <li>Data loss, corruption, or unauthorized access</li>
                <li>System failures or technical issues</li>
                <li>Any damages arising from your reliance on App data or calculations</li>
              </ul>
              <p className="text-red-800 leading-relaxed mt-4">
                Our total liability to you for any claims arising from your use of the App shall not exceed the amount 
                you paid for the App, if any, or $100, whichever is greater.
              </p>
            </div>
          </section>

          {/* Disclaimers */}
          <section>
            <h2 className="text-2xl font-semibold text-textBase mb-4">Disclaimers</h2>
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-textBase mb-2">No Financial Advice</h3>
                <p className="text-sm text-grey-600">
                  The App provides tools for tracking financial goals but does not constitute financial, investment, 
                  or tax advice. Always consult with qualified professionals for financial decisions.
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-textBase mb-2">No Guarantees</h3>
                <p className="text-sm text-grey-600">
                  We do not guarantee that the App will meet your specific requirements or that it will be error-free, 
                  secure, or available at all times.
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-textBase mb-2">Third-Party Services</h3>
                <p className="text-sm text-grey-600">
                  The App may integrate with third-party services. We are not responsible for the content, privacy 
                  policies, or practices of any third-party services.
                </p>
              </div>
            </div>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-2xl font-semibold text-textBase mb-4">Termination</h2>
            <p className="text-grey-600 leading-relaxed">
              We may terminate or suspend your account and access to the App immediately, without prior notice, 
              for any reason, including breach of these Terms. Upon termination, your right to use the App will 
              cease immediately, and we may delete your account and associated data.
            </p>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2 className="text-2xl font-semibold text-textBase mb-4">Changes to Terms</h2>
            <p className="text-grey-600 leading-relaxed">
              We reserve the right to modify these Terms at any time. We will notify users of any material changes 
              by posting the new Terms in the App or by other reasonable means. Your continued use of the App after 
              such modifications constitutes acceptance of the updated Terms.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-semibold text-textBase mb-4">Governing Law</h2>
            <p className="text-grey-600 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the jurisdiction where 
              FinInteract is incorporated, without regard to its conflict of law provisions.
            </p>
          </section>

          {/* Contact Information */}
          <section>
            <h2 className="text-2xl font-semibold text-textBase mb-4">Contact Information</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-grey-600 mb-4">
                If you have any questions about these Terms & Conditions, please contact us:
              </p>
              <div className="space-y-2 text-grey-600">
                <p><strong>Email:</strong> legal@fininteract.com</p>
                <p><strong>Location:</strong> Remote</p>
              </div>
            </div>
          </section>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsConditions;
